import React, { useContext, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { media } from 'context';
import { APP_BANNER_HEIGHT, HEADER_Z_INDEX, Icon, Layout, Link, Text } from 'ds';
import { CUSTOMER_SUPPORT_PATH } from 'routes';
import { selectNeedsApprovalWorkOrders } from 'store/AIAssistant/selectors';
import { selectPlatformFees } from 'store/Tenancy/selectors';
import { actions } from 'store/UI';
import { selectUIState } from 'store/UI/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserV2 } from 'store/v2/User/selectors';
import { userCanViewProposal } from 'ux/Tenant/PlatformFeeProposal/utils';

interface Props {}

const TenantWorkOrderActionRequiredBanner: React.FC<Props> = () => {
  const history = useHistory();
  const { isMobile } = useContext(media);
  const user = useAppSelector(selectUserV2);
  const pathname = useLocation().pathname;
  const platformFees = useAppSelector(selectPlatformFees);
  const platformFeeProposal = platformFees?.find(
    pf => !!pf.sent_at && !pf.approved_at && userCanViewProposal(pf, user?.id)
  );
  const needsApprovalWorkOrders = useAppSelector(selectNeedsApprovalWorkOrders);
  const { appBannerType } = useAppSelector(selectUIState);
  const dispatch = useAppDispatch();
  const firstNeedsApprovalWorkOrder = needsApprovalWorkOrders[0];
  const currentPageIsFirstNeedsApprovalWorkOrderPage =
    firstNeedsApprovalWorkOrder &&
    pathname ===
      `${CUSTOMER_SUPPORT_PATH}/${firstNeedsApprovalWorkOrder.support_ticket_id}/orders/${firstNeedsApprovalWorkOrder.id}`;

  const getBannerAttributes = () => {
    if (!user) return;

    if (
      needsApprovalWorkOrders === null ||
      needsApprovalWorkOrders.length === 0 ||
      currentPageIsFirstNeedsApprovalWorkOrderPage ||
      platformFeeProposal
    )
      return;

    return {
      type: 'needs_approval_work_order'
    } as const;
  };

  const bannerAttributes = getBannerAttributes();
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!bannerAttributes) {
      if (appBannerType === 'needs_approval_work_order' && needsApprovalWorkOrders.length === 0) {
        dispatch(actions.updateUIState({ appBannerType: null }));
      }
    } else {
      dispatch(actions.updateUIState({ appBannerType: bannerAttributes.type }));
    }
  }, [bannerAttributes?.type, appBannerType, dispatch, needsApprovalWorkOrders.length, pathname, platformFeeProposal]);

  if (!bannerAttributes) return null;

  if (appBannerType !== null && appBannerType !== bannerAttributes.type) return null;

  let content: JSX.Element | null = null;

  const orderPath = isMobile
    ? `${CUSTOMER_SUPPORT_PATH}/${firstNeedsApprovalWorkOrder.support_ticket_id}/details?order_id=${firstNeedsApprovalWorkOrder.id}`
    : `${CUSTOMER_SUPPORT_PATH}/${firstNeedsApprovalWorkOrder.support_ticket_id}/orders/${firstNeedsApprovalWorkOrder.id}`;

  if (bannerAttributes.type === 'needs_approval_work_order') {
    content = (
      <Link onClick={() => history.push(orderPath)} opacity={false} fullWidth color="gray-900">
        <Layout
          color="red-200"
          height={APP_BANNER_HEIGHT}
          align="center"
          justify="center"
          paddingX={isMobile ? 24 : undefined}
          paddingY={12}
        >
          <Icon name="warning" stroke={2} size="xs" />
          <Layout marginLeft={12}>
            <Text size="body-xs" semibold>
              Action needed to resolve your request
            </Text>
          </Layout>
          <Layout direction="row" gap={8} align="center" marginLeft={20} display="inline-flex">
            <Text size="body-xs" semibold>
              Review
            </Text>
            <Icon name="rightChevron" size="xxs" color="gray-900" stroke={2} />
          </Layout>
        </Layout>
      </Link>
    );
  }

  if (!content) return null;

  return (
    <Layout position="fixed" top={0} left={0} right={0} zIndex={HEADER_Z_INDEX} width="100%">
      {content}
    </Layout>
  );
};

export default TenantWorkOrderActionRequiredBanner;
